import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IUserModulesByOfferingQuery } from 'app/library/course-offering-catalog/interfaces/user-modules-by-offering.query.interface';
import { ILandingPageComponentQuery } from 'app/library/landing-page-component/interfaces/landing-page-component-query.interface';
import { LandingPageComponent } from 'app/library/landing-page-component/models/landing-page-component.model';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICourseOfferingLandingPageOverviewQuery } from '../interfaces/course-offering-landing-page-overview.query.interface';
import { CourseOfferingLandingPageOverview } from '../models/course-offering-landing-page-overview.model';
import { CourseOfferingLandingPageCommand } from '../models/course-offering-landing-page.command.model';
import { CourseOfferingLandingPage } from '../models/course-offering-landing-page.model';
import { ICourseOfferingLandingPageQuery } from '../interfaces/course-offering-landing-page.query.interface';
import { IListingBookingRequestApproverQuery } from '../interfaces/listing-booking-request-approver.query.interface';
import { ListingBookingRequestApprover } from '../models/listing-booking-request-approver.model';
import { ICreateListingBookingRequestPayload } from '../interfaces/create-listing-booking-request.payload.interface';
import { ICatalogListingBookingQuery } from '../interfaces/catalog-listing-booking.query.interface';
import { CatalogListingBooking } from '../models/catalog-listing-booking.model';
import { ICatalogListingBookingDetailsQuery } from '../interfaces/catalog-listing-booking-details.query.interface';
import { CatalogListingBookingDetails } from '../models/catalog-listing-booking-details.model';
import { ICancelOwnBookingRequestPayload } from '../interfaces/cancel-own-booking-request.payload.interface';
import { IListingBookingRequestActionPayload } from '../interfaces/listing-booking-request-action.payload.interface';
import { ICancelListingBookingRequestsPayload } from '../interfaces/cancel-listing-booking-requests.payload.interface';
import { ICancelListingBookingRequestsQuery } from '../interfaces/cancel-listing-booking-requests.query.interface';
import { IListingBookingRequestCancelPreviewQuery } from '../interfaces/listing-booking-request-cancel-preview.query.interface';
import { INotifyListingBookingRequestsPayload } from '../interfaces/notify-listing-booking-requests.payload.interface';
import { IUpdateListingBookingRequestsPricePayload } from '../interfaces/update-listing-booking-requests-price.payload.interface';
import { IUpdateListingBookingRequestsPriceQuery } from '../interfaces/update-listing-booking-requests-price.query.interface';
import { IListingBookingRequestsResponseQuery } from 'app/library/course-catalog-listing/interfaces/listing-booking-requests-response.query.interface';
import { IListingBookableTeamQuery } from '../interfaces/listing-bookable-team.query.interface';
import { IListingBookableUserQuery } from '../interfaces/listing-bookable-user.query.interface';
import { ICreateListingBookingRequestsForUserPayload } from '../interfaces/create-listing-booking-requests-for-user.payload.interface';
import { ICreateListingBookingRequestsForUserQuery } from '../interfaces/create-listing-booking-requests-for-user.query.interface';
import { CreateListingBookingRequestsForUser } from '../models/create-listing-booking-requests-for-user.model';
import { IUpdateListingBookingRequestApproversPayload } from '../interfaces/update-listing-booking-request-approvers.payload.interface';
import { ListingBookingRequestsResponse } from '../models/listing-booking-requests-response.model';
import { IMoveTargetListingsResponseQuery } from '../interfaces/move-target-listings-response.query.interface';
import { IMoveListingBookingRequestsPayload } from '../interfaces/move-listing-booking-requests.payload.interface';
import { IMoveListingBookingRequestsQuery } from '../interfaces/move-listing-booking-requests.query.interface';
import { IPaginatedResponse } from 'app/projects/subentity/src/lib/interfaces/paginated-response.interface';
import { IListingBookableUsersResponseQuery } from '../interfaces/listing-bookable-users-response.query.interface';
import { IListingBookableUsersResponse } from '../interfaces/listing-bookable-users-response.interface';
import { ListingBookableUser } from '../models/listing-bookable-user.model';
import { IAsyncJobIdResponse } from '../interfaces/async-job-id-response.interface';
import { IListingBookingRequestsActionPayload } from '../interfaces/listing-booking-requests-action.payload.interface';
import { ICreateListingBookingRequestsForTeamPayload } from '../interfaces/create-listing-booking-requests-for-team.payload.interface';
import { IListingBookingRequestsPreviewForTeamPayload } from '../interfaces/listing-booking-requests-preview-for-team.payload.interface';
import { IListingBookingRequestsPreviewForTeamQuery } from '../interfaces/listing-booking-requests-preview-for-team.query.interface';
import { ICourseOfferingMeBookingQuery } from '../interfaces/course-offering-me-booking.query.interface';
import { CourseOfferingMeBooking } from '../models/course-offering-me-booking.model';

@Injectable()
export class CourseOfferingLandingPageApiService extends EntityService<ICourseOfferingLandingPageOverviewQuery> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {
        super(http, environment.apiUrl, 'course-offerings');
    }

    getCourseOfferingLandingPage(courseOfferingId: string): Observable<CourseOfferingLandingPage> {
        return this.http
            .get<ICourseOfferingLandingPageQuery>(`${this.apiUrl}/${this.entities}/${courseOfferingId}/landing-page`)
            .pipe(map((query) => new CourseOfferingLandingPage(query)));
    }

    getCourseOfferingLandingPageOverview(courseOfferingId: string): Observable<CourseOfferingLandingPageOverview> {
        return this.http
            .get<ICourseOfferingLandingPageOverviewQuery>(`${this.apiUrl}/${this.entities}/${courseOfferingId}/landing-page/overview`)
            .pipe(map((query) => new CourseOfferingLandingPageOverview(query)));
    }

    getCourseOfferingLandingPageComponent(courseOfferingId: string, componentId: string): Observable<LandingPageComponent> {
        return this.http
            .get<ILandingPageComponentQuery>(`${this.apiUrl}/${this.entities}/${courseOfferingId}/landing-page/components/${componentId}`)
            .pipe(map((query) => new LandingPageComponent(query)));
    }

    getUserModulesByOffering(userId: string, courseOfferingId: string, page = 1, limit = 100): Observable<IUserModulesByOfferingQuery> {
        const params = new HttpParams().set('page', page.toString()).set('limit', limit.toString()).set('filter[offeringId]', courseOfferingId);

        return this.http.get<IUserModulesByOfferingQuery>(`${this.apiUrl}/users/${userId}/modules`, { params });
    }

    getMyCourseOfferingBookings(courseOfferingId: string): Observable<CourseOfferingMeBooking[]> {
        return this.http
            .get<ICourseOfferingMeBookingQuery[]>(`${this.apiUrl}/me/course-offerings/${courseOfferingId}/bookings`)
            .pipe(map((queries) => (queries || []).map((query) => new CourseOfferingMeBooking(query))));
    }

    getListingBookingRequestApproversByUser(userId: string): Observable<ListingBookingRequestApprover[]> {
        return this.http
            .get<IListingBookingRequestApproverQuery[]>(`${this.apiUrl}/users/${userId}/booking-request/approvers`)
            .pipe(map((queries) => (queries || []).map((query) => new ListingBookingRequestApprover(query))));
    }

    getListingBookingRequestApproversByTeam(teamId: string, userIds: number[] = []): Observable<ListingBookingRequestApprover[]> {
        let params = new HttpParams();

        if (userIds?.length) {
            params = params.set('user_ids', userIds.join(','));
        }

        return this.http
            .get<IListingBookingRequestApproverQuery[]>(`${this.apiUrl}/teams/${teamId}/booking-request/approvers`, { params })
            .pipe(map((queries) => (queries || []).map((query) => new ListingBookingRequestApprover(query))));
    }

    getListingBookingRequestApproversByListing(listingId: string, userIds: number[] = []): Observable<ListingBookingRequestApprover[]> {
        let params = new HttpParams();

        if (userIds?.length) {
            params = params.set('user_ids', userIds.join(','));
        }

        return this.http
            .get<IListingBookingRequestApproverQuery[]>(`${this.apiUrl}/catalog/listings/${listingId}/booking-request/approvers`, { params })
            .pipe(map((queries) => (queries || []).map((query) => new ListingBookingRequestApprover(query))));
    }

    createListingBookingRequest(listingId: string, payload?: ICreateListingBookingRequestPayload | null): Observable<CatalogListingBooking> {
        return this.http
            .post<ICatalogListingBookingQuery>(`${this.apiUrl}/catalog/listings/${listingId}/booking-requests`, payload || {})
            .pipe(map((query) => new CatalogListingBooking(query)));
    }

    createListingBookingRequestsForUsers(listingId: string, payload: ICreateListingBookingRequestsForUserPayload): Observable<CreateListingBookingRequestsForUser> {
        return this.http
            .post<ICreateListingBookingRequestsForUserQuery>(`${this.apiUrl}/catalog/listings/${listingId}/booking-requests/for-user`, payload)
            .pipe(map((query) => new CreateListingBookingRequestsForUser(query)));
    }

    createListingBookingRequestsForTeam(listingId: string, payload: ICreateListingBookingRequestsForTeamPayload): Observable<IAsyncJobIdResponse> {
        return this.http.post<IAsyncJobIdResponse>(`${this.apiUrl}/catalog/listings/${listingId}/booking-requests/for-team`, payload);
    }

    previewListingBookingRequestsForTeam(
        listingId: string,
        payload: IListingBookingRequestsPreviewForTeamPayload
    ): Observable<IListingBookingRequestsPreviewForTeamQuery> {
        return this.http.post<IListingBookingRequestsPreviewForTeamQuery>(
            `${this.apiUrl}/catalog/listings/${listingId}/booking-requests/preview-for-team`,
            payload
        );
    }

    getListingBookingRequest(bookingRequestId: string): Observable<CatalogListingBookingDetails> {
        return this.http
            .get<ICatalogListingBookingDetailsQuery>(`${this.apiUrl}/catalog/booking-requests/${bookingRequestId}`)
            .pipe(map((query) => new CatalogListingBookingDetails(query)));
    }

    updateListingBookingRequestApprovers(bookingRequestId: string, payload: IUpdateListingBookingRequestApproversPayload): Observable<CatalogListingBookingDetails> {
        return this.http
            .put<ICatalogListingBookingDetailsQuery>(`${this.apiUrl}/catalog/booking-requests/${bookingRequestId}/approvers`, payload)
            .pipe(map((query) => new CatalogListingBookingDetails(query)));
    }

    approveListingBookingRequest(bookingRequestId: string, payload: IListingBookingRequestActionPayload): Observable<CatalogListingBooking> {
        return this.http
            .post<ICatalogListingBookingQuery>(`${this.apiUrl}/catalog/booking-requests/${bookingRequestId}/approve`, payload)
            .pipe(map((query) => new CatalogListingBooking(query)));
    }

    putListingBookingRequestOnWaitingList(bookingRequestId: string, payload: IListingBookingRequestActionPayload): Observable<CatalogListingBooking> {
        return this.http
            .post<ICatalogListingBookingQuery>(`${this.apiUrl}/catalog/booking-requests/${bookingRequestId}/waiting-list`, payload)
            .pipe(map((query) => new CatalogListingBooking(query)));
    }

    rejectListingBookingRequest(bookingRequestId: string, payload: IListingBookingRequestActionPayload): Observable<CatalogListingBooking> {
        return this.http
            .post<ICatalogListingBookingQuery>(`${this.apiUrl}/catalog/booking-requests/${bookingRequestId}/reject`, payload)
            .pipe(map((query) => new CatalogListingBooking(query)));
    }

    approveListingBookingRequests(listingId: string, payload: IListingBookingRequestsActionPayload): Observable<IAsyncJobIdResponse> {
        return this.http.post<IAsyncJobIdResponse>(`${this.apiUrl}/catalog/listings/${listingId}/booking-requests/approve`, payload);
    }

    putListingBookingRequestsOnWaitingList(listingId: string, payload: IListingBookingRequestsActionPayload): Observable<IAsyncJobIdResponse> {
        return this.http.post<IAsyncJobIdResponse>(`${this.apiUrl}/catalog/listings/${listingId}/booking-requests/waiting-list`, payload);
    }

    rejectListingBookingRequests(listingId: string, payload: IListingBookingRequestsActionPayload): Observable<IAsyncJobIdResponse> {
        return this.http.post<IAsyncJobIdResponse>(`${this.apiUrl}/catalog/listings/${listingId}/booking-requests/reject`, payload);
    }

    cancelListingBookingRequest(bookingRequestId: string, payload: ICancelOwnBookingRequestPayload): Observable<CatalogListingBooking> {
        return this.http
            .post<ICatalogListingBookingQuery>(`${this.apiUrl}/catalog/booking-requests/${bookingRequestId}/cancel`, payload)
            .pipe(map((query) => new CatalogListingBooking(query)));
    }

    cancelListingBookingRequests(payload: ICancelListingBookingRequestsPayload): Observable<ICancelListingBookingRequestsQuery> {
        return this.http.post<ICancelListingBookingRequestsQuery>(`${this.apiUrl}/catalog/booking-requests/cancel/bulk`, payload);
    }

    getListingBookingRequestCancelPreview(bookingRequestId: number): Observable<IListingBookingRequestCancelPreviewQuery> {
        return this.http.get<IListingBookingRequestCancelPreviewQuery>(`${this.apiUrl}/catalog/booking-requests/${bookingRequestId}/cancel-preview`);
    }

    notifyListingBookingRequests(payload: INotifyListingBookingRequestsPayload): Observable<void> {
        return this.http.post<void>(`${this.apiUrl}/catalog/booking-requests/notify`, payload);
    }

    fetchNotificationKeywords(): Observable<string[]> {
        return this.http.get<string[]>(`${this.apiUrl}/notification/keywords`);
    }

    updateListingBookingRequestsPrice(payload: IUpdateListingBookingRequestsPricePayload): Observable<IUpdateListingBookingRequestsPriceQuery> {
        return this.http.patch<IUpdateListingBookingRequestsPriceQuery>(`${this.apiUrl}/catalog/booking-requests/price`, payload);
    }

    getMoveTargetListings(bookingRequestId: number, page = 1, limit = 10): Observable<IMoveTargetListingsResponseQuery> {
        const params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());
        return this.http.get<IMoveTargetListingsResponseQuery>(`${this.apiUrl}/catalog/booking-requests/${bookingRequestId}/move-target-listings`, { params });
    }

    moveListingBookingRequests(payload: IMoveListingBookingRequestsPayload): Observable<IMoveListingBookingRequestsQuery> {
        return this.http.post<IMoveListingBookingRequestsQuery>(`${this.apiUrl}/catalog/booking-requests/move`, payload);
    }

    getListingBookingRequests(
        listingId: string,
        page = 1,
        limit = 10,
        searchTerm = '',
        teamIds: string[] = [],
        includeSubteams = true,
        statuses: string[] = [],
        excludeSelf = true,
        sortBy = '',
        sortOrder = '',
        groupBy: 'execution' | null = null
    ): Observable<ListingBookingRequestsResponse> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('limit', limit.toString())
            .set('includeSubteams', includeSubteams.toString());

        if (excludeSelf) {
            params = params.set('filter[excludeSelf]', 'true');
        }

        if (searchTerm) {
            params = params.set('filter[query]', searchTerm);
        }

        if (teamIds?.length) {
            teamIds.forEach((teamId) => {
                params = params.append('filter[team]', teamId);
            });
        }

        if (statuses?.length) {
            params = params.set('filter[status]', statuses.join(','));
        }

        if (sortBy && sortOrder) {
            params = params.set('sortBy', sortBy);
            params = params.set('sortOrder', sortOrder.toUpperCase());
        }

        if (groupBy) {
            params = params.set('groupBy', groupBy);
        }

        return this.http
            .get<IListingBookingRequestsResponseQuery>(`${this.apiUrl}/catalog/listings/${listingId}/booking-requests`, { params })
            .pipe(map((query) => new ListingBookingRequestsResponse(query)));
    }

    getRootTeamsWithTrainingResponsibles(): Observable<IListingBookableTeamQuery[]> {
        const params = new HttpParams().set('include', 'trainingResponsibles');
        return this.http.get<IListingBookableTeamQuery[]>(`${this.apiUrl}/root-teams`, { params });
    }

    getTeamChildrenWithTrainingResponsibles(teamId: string): Observable<IListingBookableTeamQuery[]> {
        const params = new HttpParams().set('include', 'trainingResponsibles');
        return this.http.get<IListingBookableTeamQuery[]>(`${this.apiUrl}/teams/${teamId}/children`, { params });
    }

    searchTeamsWithTrainingResponsibles(page = 1, limit = 10, searchTerm = ''): Observable<IListingBookableTeamQuery[]> {
        let params = new HttpParams().set('page', page.toString()).set('limit', limit.toString()).set('include', 'trainingResponsibles');

        if (searchTerm?.trim()) {
            params = params.set('filter[query]', searchTerm.trim());
        }

        return this.http.get<IListingBookableTeamQuery[]>(`${this.apiUrl}/teams/search`, { params });
    }

    getListingTeamBookableUsers(listingId: string, teamId: string): Observable<ListingBookableUser[]> {
        return this.http
            .get<IListingBookableUserQuery[] | IPaginatedResponse<IListingBookableUserQuery>>(`${this.apiUrl}/catalog/listings/${listingId}/teams/${teamId}/bookable-users`)
            .pipe(map((response) => this.mapBookableUserQueriesToModels(this.extractBookableUsers(response))));
    }

    searchListingBookableUsers(listingId: string, page = 1, limit = 10, searchTerm = ''): Observable<IListingBookableUsersResponse> {
        let params = new HttpParams();
        params = params.set('page', page.toString()).set('limit', limit.toString());
        if (searchTerm?.trim()) {
            params = params.set('filter[query]', searchTerm.trim());
        }

        return this.http.get<IListingBookableUserQuery[] | IListingBookableUsersResponseQuery>(`${this.apiUrl}/catalog/listings/${listingId}/bookable-users`, { params }).pipe(
            map((response) => {
                const normalized = this.normalizeBookableUsersResponse(response);
                return {
                    data: this.mapBookableUserQueriesToModels(normalized.data),
                    pagination: normalized.pagination,
                };
            })
        );
    }

    private extractBookableUsers(response: IListingBookableUserQuery[] | IPaginatedResponse<IListingBookableUserQuery>): IListingBookableUserQuery[] {
        if (Array.isArray(response)) {
            return response;
        }

        return response?.data || [];
    }

    private normalizeBookableUsersResponse(response: IListingBookableUserQuery[] | IListingBookableUsersResponseQuery): IListingBookableUsersResponseQuery {
        if (Array.isArray(response)) {
            return {
                data: response,
                pagination: {
                    number_of_pages: 1,
                    number_of_results: response.length,
                },
            };
        }

        return {
            data: response?.data || [],
            pagination: {
                number_of_pages: response?.pagination?.number_of_pages || 0,
                number_of_results: response?.pagination?.number_of_results || 0,
            },
        };
    }

    private mapBookableUserQueriesToModels(users: IListingBookableUserQuery[]): ListingBookableUser[] {
        return users.map((user) => new ListingBookableUser(user));
    }

    bookListing(listingId: string): Observable<CatalogListingBooking> {
        return this.http.post<ICatalogListingBookingQuery>(`${this.apiUrl}/catalog/listings/${listingId}/book`, {}).pipe(map((query) => new CatalogListingBooking(query)));
    }

    updateCourseOfferingCatalogLandingPage(landingPageId: string, entity: CourseOfferingLandingPageOverview): Observable<CourseOfferingLandingPageOverview> {
        const data = new CourseOfferingLandingPageCommand(entity);
        return this.http
            .patch<ICourseOfferingLandingPageOverviewQuery>(`${this.apiUrl}/course-offering-landing-pages/${landingPageId}`, data)
            .pipe(map((query) => new CourseOfferingLandingPageOverview(query)));
    }

    updateCourseOfferingCatalogLandingPageStatus(landingPageId: string, status: string): Observable<CourseOfferingLandingPageOverview> {
        const data = { status };
        return this.http
            .patch<ICourseOfferingLandingPageOverviewQuery>(`${this.apiUrl}/course-offering-landing-pages/${landingPageId}/status`, data)
            .pipe(map((query) => new CourseOfferingLandingPageOverview(query)));
    }
}
